import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaRegClock } from '@react-icons/all-files/fa/FaRegClock';

import Button from '../../../../../../components/ui/buttons/Button';
import { formatExpiresIn, formatMessageTime } from '../../../../../../utils/dates';
import { formatMoney } from '../../../../../../utils/helpers';
import OfferLinkButton from '../offers/OfferLinkButton';
import { OfferActionKind, OfferCardView } from '../offers/utils';
import OfferNote from './OfferNote';

type LiveOfferCardProps = {
  card: OfferCardView;
  onAction: (kind: OfferActionKind, card: OfferCardView) => void;
};

const LiveOfferCard = ({ card, onAction }: LiveOfferCardProps) => {
  const isDriverCounter = card.driverCounter != null;
  const amount = isDriverCounter ? card.driverCounterAmount ?? card.amount : card.amount;
  const note = isDriverCounter ? card.driverCounter?.notes : card.offer.notes;
  const timestamp = isDriverCounter
    ? card.driverCounter?.createdAt ?? card.offer.createdAt
    : card.offer.createdAt;
  const expiration = isDriverCounter ? null : formatExpiresIn(card.offer.expiresAt);

  const label = isDriverCounter
    ? 'You countered'
    : card.offer.type === 'dealer_counter'
    ? 'The buyer countered'
    : 'The buyer offered';

  return (
    <Flex direction="column" align={isDriverCounter ? 'flex-end' : 'flex-start'}>
      <Box
        maxW={{ base: '100%', md: '82%' }}
        borderWidth="1px"
        borderColor="gainsboro"
        borderRadius="11px"
        bg="white"
        px="15px"
        py="12px"
      >
        <Flex align="center" gap="8px" mb="6px">
          <Text
            as="span"
            fontFamily="heading"
            fontSize="10.5px"
            fontWeight="700"
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="leaseEndBlue"
            noOfLines={1}
          >
            {label}
          </Text>
          {expiration && (
            <HStack
              as="span"
              spacing="4px"
              ml="auto"
              flexShrink={0}
              color={expiration.isUrgent ? 'leaseEndRed' : 'grayMedium'}
              fontSize="10.5px"
              fontWeight="600"
              letterSpacing="0.04em"
            >
              <Icon as={FaRegClock} boxSize="11px" />
              <span>{expiration.label}</span>
            </HStack>
          )}
        </Flex>

        <Flex align="center" wrap="wrap" gap="10px">
          <Text
            as="span"
            fontSize="27px"
            fontWeight="700"
            lineHeight="1.1"
            color="leaseEndBlue"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatMoney(amount)}
          </Text>

          {isDriverCounter ? (
            <HStack spacing={0} ml="auto">
              <OfferLinkButton onClick={() => onAction('adjust', card)}>Adjust</OfferLinkButton>
              <Text as="span" mx="8px" color="oceanBoatBlue" opacity={0.35} userSelect="none">
                |
              </Text>
              <OfferLinkButton onClick={() => onAction('withdraw', card)}>Withdraw</OfferLinkButton>
            </HStack>
          ) : (
            <HStack spacing="14px" ml="auto">
              <OfferLinkButton onClick={() => onAction('counter', card)}>Counter</OfferLinkButton>
              <Button
                onClick={() => onAction('accept', card)}
                leftIcon={<Icon as={FaCheck} boxSize="12px" />}
                bg="leaseEndYellow"
                color="leaseEndBlue"
                borderRadius="full"
                fontSize="12.5px"
                fontWeight="800"
                letterSpacing="0.04em"
                px="18px"
                py="10px"
                h="auto"
                _hover={{ bg: 'yellowLight' }}
              >
                Accept {formatMoney(amount)}
              </Button>
            </HStack>
          )}
        </Flex>

        <OfferNote note={note} marginTop="9px" />
      </Box>
      <Text
        mt="3px"
        fontSize="10.5px"
        color="grayMedium"
        textAlign={isDriverCounter ? 'right' : 'left'}
      >
        {formatMessageTime(timestamp)}
      </Text>
    </Flex>
  );
};

export default LiveOfferCard;
